import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import CmsSwitcherBar from "@/components/app/CmsSwitcherBar";
import {
  Building2,
  ArrowRight,
  Loader2,
  ShieldCheck,
  Clock,
  X,
} from "lucide-react";

/**
 * /uni-portal — a signed-in university staff member's dashboard.
 * Lists claims (approved + pending) so they can open the edit pages.
 */
export default function UniPortal() {
  const { user, loading: authLoading, login } = useAuth();
  const [state, setState] = useState({ loading: true, items: [] });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setState({ loading: false, items: [] });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/claims/mine");
        setState({ loading: false, items: data?.items || [] });
      } catch {
        setState({ loading: false, items: [] });
      }
    })();
  }, [user, authLoading]);

  if (authLoading || state.loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-stone-500 font-mono text-sm" data-testid="uni-portal-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="uni-portal-signin">
        <div className="p-8 border border-stone-300 rounded-xl bg-white">
          <div className="inline-flex items-center gap-2 text-[10px] font-underground-sc tracking-widest text-stone-500">
            <ShieldCheck className="h-3 w-3" /> University Portal
          </div>
          <h1 className="mt-3 font-editorial text-3xl">
            Own a university? Take charge of your listing.
          </h1>
          <p className="mt-3 text-stone-600 text-sm max-w-xl leading-relaxed">
            Sign in with your work email to see the universities you can edit.
            Applicants read a "verified by university" mark next to every
            field you own — it builds trust in seconds.
          </p>
          <Button className="mt-5" onClick={login} data-testid="uni-portal-signin-btn">
            Sign in <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  const approved = state.items.filter((c) => c.status === "approved");
  const pending = state.items.filter((c) => c.status === "pending");
  const rejected = state.items.filter((c) => c.status === "rejected");

  return (
    <>
      <CmsSwitcherBar />
      <div className="max-w-5xl mx-auto px-6 py-10" data-testid="uni-portal-page">
      <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
        University Portal
      </div>
      <h1 className="mt-1 font-editorial text-4xl">Your universities</h1>
      <p className="mt-2 text-stone-600 text-sm">
        Signed in as <span className="font-mono">{user.email}</span>.
      </p>

      {approved.length === 0 && pending.length === 0 && (
        <div className="mt-8 p-8 border border-dashed border-stone-300 rounded-xl text-center" data-testid="uni-portal-empty">
          <Building2 className="h-8 w-8 text-stone-400 mx-auto" />
          <div className="mt-3 font-editorial text-xl">No universities yet.</div>
          <p className="mt-2 text-sm text-stone-600 max-w-md mx-auto">
            Open any university page on Which University Advisor and click
            "Claim this university" to get started.
          </p>
          <Link to="/explore">
            <Button className="mt-4" size="sm">Browse universities</Button>
          </Link>
        </div>
      )}

      {approved.length > 0 && (
        <section className="mt-8" data-testid="uni-portal-approved-section">
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
            You own · {approved.length}
          </div>
          <ul className="mt-3 space-y-3">
            {approved.map((c) => (
              <li
                key={c.id}
                data-testid={`uni-portal-row-${c.university_id}`}
                className="p-5 border border-stone-300 rounded-lg bg-white hover:border-stone-900 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-emerald-700" />
                      <span className="font-editorial text-lg">
                        {c.university_name}
                      </span>
                    </div>
                    {c.role && <div className="mt-1 text-xs text-stone-500">{c.role}</div>}
                    <div className="mt-1 text-[10px] font-underground-sc tracking-wider text-stone-400">
                      {c.verified_via === "domain" ? "Domain-verified" : "Editor-verified"} · {new Date(c.decided_at || c.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 min-w-[130px]">
                    <Link to={`/uni-portal/uni/${c.university_id}`}>
                      <Button size="sm" data-testid={`uni-portal-edit-${c.university_id}`}>
                        Edit
                      </Button>
                    </Link>
                    <Link to={`/uni/${c.university_id}`}>
                      <Button size="sm" variant="outline">
                        Public page
                      </Button>
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {pending.length > 0 && (
        <section className="mt-10" data-testid="uni-portal-pending-section">
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
            Pending review · {pending.length}
          </div>
          <ul className="mt-3 space-y-3">
            {pending.map((c) => (
              <li
                key={c.id}
                className="p-5 border border-amber-200 rounded-lg bg-amber-50/50 flex items-start justify-between gap-4"
              >
                <div>
                  <div className="flex items-center gap-2 text-amber-900">
                    <Clock className="h-4 w-4" />
                    <span className="font-editorial text-lg">
                      {c.university_name}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-amber-900">
                    Submitted {new Date(c.created_at).toLocaleDateString()}. We'll email you at{" "}
                    <span className="font-mono">{c.email}</span> once decided.
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {rejected.length > 0 && (
        <section className="mt-10">
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
            Rejected · {rejected.length}
          </div>
          <ul className="mt-3 space-y-3">
            {rejected.map((c) => (
              <li key={c.id} className="p-5 border border-stone-200 rounded-lg bg-stone-50">
                <div className="flex items-center gap-2 text-stone-600">
                  <X className="h-4 w-4" />
                  <span>{c.university_name}</span>
                </div>
                {c.reject_reason && (
                  <div className="mt-2 text-xs text-stone-600">
                    Editor note: {c.reject_reason}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
    </>
  );
}
